<template>
	<div class="waitlist-new">
		<ModalHeader :title="getString(editingEntry ? 'edit_waitlist_entry' : 'add_to_waitlist')" :button-text="getString('save')" :button-loading="loading" :button-disabled="newDate === null" @click-button="saveEntry"/>
		<AppList :items="items">
			<template v-slot:date-edit="{item}">
				<div class="list-item-header">
					<p class="list-item-title">{{ item.title }}</p>
					<DatePicker ref="date-picker-date" v-model="newDate" mode="date" color="teal" :min-date='now' :max-date="maxDate" :is-dark="userStore.darkMode" :disabled-dates="disabledDates" @update:modelValue="changedDate">
						<template #default="{ togglePopover }">
							<AppTextInput class="text-input ml-auto" @click="togglePopover()" readonly :placeholder="getString('reservation_date')"
										  :value="newDate ? formatDateOnly(newDate, false) : null"/>
						</template>
					</DatePicker>
				</div>
			</template>
			<template v-slot:time-edit="{item}">
				<div class="list-item-header">
					<p class="list-item-title">{{ item.title }}</p>
					<AppButton v-if="dailyTimeslots.length > 0" class="ml-auto" outline :text="newTime ? formatTimeHHmm(newTime, false) : getString('time')" :loading="loadingTime" @click="timeSheetActive = true" :disabled="newDate === null" />
					<p v-else class="no-time-available">{{ getString('no_time_available') }}</p>
				</div>
			</template>
			<template #guests-slot="{item}">
				<div class="list-item-header">
					<p class="list-item-title">{{ item.title }}</p>
					<div class="touch-number-input-container">
						<TouchNumberInput :min="1" :value="item.value" @changed-value="v => { item.callbackNumber(v) }" />
					</div>
				</div>
			</template>
			<template #list-slot="{item}">
				<AppList :items="item.subitems">
					<template #dogs-slot="{item}">
						<div class="list-item-header">
							<p class="list-item-title">{{ item.title }}</p>
							<div class="touch-number-input-container">
								<TouchNumberInput :min="0" :value="item.value" @changed-value="v => { item.callbackNumber(v) }" />
							</div>
						</div>
					</template>
					<template #children-slot="{item}">
						<div class="list-item-header">
							<p class="list-item-title">{{ item.title }}</p>
							<div class="touch-number-input-container">
								<TouchNumberInput :min="0" :value="item.value" @changed-value="v => { item.callbackNumber(v) }" />
							</div>
						</div>
					</template>
				</AppList>
			</template>
		</AppList>
		<TimeSelector :active="timeSheetActive" :daily-timeslots="dailyTimeslots" :exceptions="scheduleExceptions" :availability="[]"
					  :max-capacity="9999" :selected-date="newDate || now" @toggling-popup="v => timeSheetActive = v"
					  @selected-time="setSelectedTime"/>
	</div>
</template>

<script>
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import AppList from "@/shared/components/AppList.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppButton from "@/shared/components/AppButton.vue";
import TimeSelector from "@/console-new/components/ops/tables/TimeSelector.vue";
import TouchNumberInput from "@/console-new/components/shared/TouchNumberInput.vue";
import {useUserStore} from "@/user/stores";
import {formatDateOnly, formatTimeHHmm, localToServerTime} from "@/shared/helpers/dates";
import {buildScheduleDisabledDates} from "@/shared/helpers/experiences";
import {validateEmail, validatePhone} from "@/shared/helpers/strings";
import {useExperiencesStore, useRoomsOrdersStore} from "@/console-new/stores";
import API from "@/shared/helpers/API";
import {timeslotTimes} from "@/book/helpers/availabilities";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const roomsOrdersStore = useRoomsOrdersStore();
		const userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const experiencesStore = useExperiencesStore();
		return {
			getString: languageStore.getString,
			roomsOrdersStore,
			userStore,
			notificationStore,
			experiencesStore,
			formatDateOnly,
			formatTimeHHmm
		}
	},
	name: "WaitlistNew",
	components: {AppTextInput, AppButton, TimeSelector, AppList, ModalHeader, TouchNumberInput},
	data() {
		return {
			now: new Date(),
			loading: false,
			loadingTime: false,
			newDate: new Date(),
			newTime: null,
			newExperience: null,
			newTolerance: 0,
			newPeople: null,
			newName: null,
			newEmail: null,
			newPhone: null,
			newDogs: null,
			newChildren: null,
			newNotes: null,
			timeSheetActive: false,
			scheduleTimeslots: [],
			scheduleExceptions: [],
			pendingToleranceMinutes: null,
			otherExpanded: false,
		}
	},
	computed: {
		editingEntry() {
			return this.roomsOrdersStore.manageWaitlistEntry;
		},
		dailyTimeslots() {
			if (!this.newDate || !this.scheduleTimeslots || this.scheduleTimeslots.length === 0) {
				return [];
			}
			const dateStr = formatDateOnly(this.newDate);
			const foundException = this.scheduleExceptions.find(ex => formatDateOnly(new Date(ex.date)) === dateStr);
			if (foundException) {
				let times = [];
				(foundException.timeslots || []).forEach(t => {
					let tHours = timeslotTimes(t, [], 9999, this.isDateToday, {});
					if (tHours.length > 0) {
						times.push({slot: t, times: tHours});
					}
				});
				return times;
			}
			const weekday = this.newDate.getDay();
			let timeslots = this.scheduleTimeslots.filter(t => ((t.weekday + 1) % 7) === weekday);

			let times = [];
			timeslots.forEach(t => {
				let tHours = timeslotTimes(t, [], 9999, this.isDateToday, {});
				if(tHours.length > 0) {
					times.push({slot: t, times: tHours});
				}
			});
			return times;
		},
		isDateToday() {
			return this.newDate && formatDateOnly(new Date()) === formatDateOnly(this.newDate);
		},
		selectedSlot() {
			if(!this.newTime) return null;
			let tStr = formatTimeHHmm(this.newTime, false);
			for(let entry of this.dailyTimeslots) {
				if(entry.times.find(tt => tt.time === tStr)) {
					return entry.slot;
				}
			}
			return null;
		},
		toleranceStep() {
			if(this.selectedSlot && this.selectedSlot.intervals) {
				return this.selectedSlot.intervals;
			}
			const daySlot = this.dailyTimeslots.find(entry => entry.slot.intervals);
			if(daySlot) {
				return daySlot.slot.intervals;
			}
			const anySlot = this.scheduleTimeslots.find(t => t.intervals);
			if(anySlot) {
				return anySlot.intervals;
			}
			return 15;
		},
		toleranceLabels() {
			return [
				this.getString('waitlist_tolerance_exact'),
				"± "+this.toleranceStep+" min",
				"± "+(this.toleranceStep * 3)+" min",
			];
		},
		items() {
			return [
				{
					title: this.getString('experience'),
					selectValues: this.experiencesStore.businessExperiences ? this.experiencesStore.businessExperiences.map(e => e.name) : [],
					selectedIndex: this.experiencesStore.businessExperiences ? this.experiencesStore.businessExperiences.findIndex(e => e.experience_id === this.newExperience) : null,
					callbackSelect: (index) => {
						this.newExperience = this.experiencesStore.businessExperiences[index].experience_id;
						if(this.newTime) {
							this.newTime = null;
						}
						if(this.newDate) {
							this.checkDateAvailable();
						}
					},
				},
				{
					title: this.getString('reservation_date'),
					customSlot: 'date-edit',
				},
				{
					title: this.getString('desired_time'),
					customSlot: 'time-edit',
				},
				{
					title: this.getString('tolerance'),
					selectValues: this.toleranceLabels,
					selectedIndex: [0, 1, 3].indexOf(this.newTolerance),
					callbackSelect: (index) => {
						this.newTolerance = [0, 1, 3][index];
					},
				},
				{
					title: this.getString('guests'),
					customSlot: 'guests-slot',
					value: this.newPeople,
					callbackNumber: (val) => {
						this.newPeople = val;
					},
				},
				{
					title: this.getString('guest_name'),
					textInput: true,
					placeholder: this.getString('guest_name'),
					value: this.newName,
					callbackText: (val) => {
						this.newName = val;
					},
				},
				{
					title: this.getString('email'),
					textInput: true,
					placeholder: this.getString('email'),
					type: 'email',
					value: this.newEmail,
					callbackText: (val) => {
						this.newEmail = val;
					},
				},
				{
					title: this.getString('phone'),
					textInput: true,
					placeholder: this.getString('phone'),
					type: 'tel',
					value: this.newPhone,
					callbackText: (val) => {
						this.newPhone = val;
					},
				},
				{
					title: this.getString('other'),
					dropdownSlot: 'list-slot',
					chevronItem: true,
					active: this.otherExpanded,
					callback: (val) => {
						this.otherExpanded = val;
					},
					subitems: [
						{
							title: this.getString('dogs'),
							customSlot: 'dogs-slot',
							value: this.newDogs,
							callbackNumber: (val) => {
								this.newDogs = val;
							},
						},
						{
							title: this.getString('children'),
							customSlot: 'children-slot',
							value: this.newChildren,
							callbackNumber: (val) => {
								this.newChildren = val;
							},
						},
						{
							title: this.getString('notes'),
							textInput: true,
							isTextArea: true,
							placeholder: this.getString('notes'),
							value: this.newNotes || "",
							callbackText: (val) => {
								this.newNotes = val;
							},
						},
					]
				},
			]
		},
		maxDate() {
			let maxDate = new Date();
			maxDate.setDate(maxDate.getDate() + (this.currentExperience && this.currentExperience.days_advance ? this.currentExperience.days_advance : 30));
			return maxDate;
		},
		currentExperience() {
			return this.experiencesStore.businessExperiences
				? this.experiencesStore.businessExperiences.find(e => e.experience_id === this.newExperience)
				: null;
		},
		disabledDates() {
			const daysAdvance = this.currentExperience?.days_advance || 30;
			return buildScheduleDisabledDates(this.scheduleExceptions, this.scheduleTimeslots, daysAdvance);
		},
	},
	methods: {
		checkDateAvailable() {
			if (!this.newDate) return;

			const selectedDate = new Date(this.newDate);
			const selDay = selectedDate.getDay() + 1;

			const isDateDisabled = this.disabledDates.some(disabledEntry => {
				if (disabledEntry instanceof Date) {
					return selectedDate.toDateString() === disabledEntry.toDateString();
				}

				if (disabledEntry.repeat && Array.isArray(disabledEntry.repeat.weekdays)) {
					return disabledEntry.repeat.weekdays.includes(selDay);
				}

				return false;
			});

			if (isDateDisabled) {
				this.newDate = null;
			}
		},
		changedDate() {
			if(this.newTime) {
				this.newTime = null;
			}
		},
		setSelectedTime(time) {
			if (!time) return;
			const parts = time.split(":");
			if (parts.length >= 2) {
				const d = new Date(this.newDate);
				d.setHours(parseInt(parts[0]), parseInt(parts[1]), 0, 0);
				this.newTime = d;
			}
		},
		loadExperienceData() {
			if (!this.newExperience) {
				this.scheduleTimeslots = [];
				this.scheduleExceptions = [];
				return;
			}

			const schedulePromise = new Promise((resolve, reject) => {
				API.init().getExperienceSchedule(
					this.userStore.currBusinessId,
					this.newExperience,
					res => resolve(res),
					err => reject(err)
				);
			});

			const exceptionsPromise = new Promise((resolve, reject) => {
				API.init().getExperienceExceptions(
					this.userStore.currBusinessId,
					this.newExperience,
					res => resolve(res),
					err => reject(err)
				);
			});

			this.loadingTime = true;
			Promise.all([schedulePromise, exceptionsPromise])
				.then(([scheduleRes, exceptionsRes]) => {

					this.loadingTime = false;

					this.scheduleTimeslots = scheduleRes?.timeslots
						? scheduleRes.timeslots.map(t =>
							this.experiencesStore.normalizeTimeslot(t)
						)
						: [];

					if (exceptionsRes?.exceptions) {
						const exMap = {};
						exceptionsRes.exceptions_timeslots.forEach(t => {
							if (!t.experience_timeslot_exception_id) return;
							const id = t.experience_timeslot_exception_id;
							(exMap[id] ??= []).push(
								this.experiencesStore.normalizeTimeslot(t)
							);
						});
						this.scheduleExceptions = exceptionsRes.exceptions.map(e => ({
							experience_timeslot_exception_id: Number(
								e.experience_timeslot_exception_id
							),
							date: e.date,
							timeslots: exMap[e.experience_timeslot_exception_id] ?? []
						}));
					} else {
						this.scheduleExceptions = [];
					}

					if(this.newDate) {
						this.checkDateAvailable();
					}
				})
				.catch(err => {
					console.error('Failed to load experience data:', err);
					this.scheduleTimeslots = [];
					this.scheduleExceptions = [];
					this.loadingTime = false;
				});
		},
		saveEntry() {

			if (!this.newExperience) {
				this.notificationStore.showNotification("invalid-experience");
				return;
			}
			if (!this.newDate || !this.newTime) {
				this.notificationStore.showNotification(!this.newDate ? "invalid-date" : "invalid-time");
				return;
			}
			if (!this.newPeople || this.newPeople <= 0) {
				this.notificationStore.showNotification("invalid-people");
				return;
			}
			if (!this.newName) {
				this.notificationStore.showNotification("invalid-name");
				return;
			}
			if (this.newEmail && !validateEmail(this.newEmail)) {
				this.notificationStore.showNotification("wrong-email");
				return;
			}
			if (this.newPhone && !validatePhone(this.newPhone)) {
				this.notificationStore.showNotification("wrong-phone");
				return;
			}

			let combined = new Date(this.newDate);
			combined.setHours(this.newTime.getHours(), this.newTime.getMinutes(), 0, 0);
			let dateServer = localToServerTime(combined);

			let data = {
				experience_id: this.newExperience,
				date: dateServer.toISOString().split('T')[0],
				time: dateServer.toTimeString().split(' ')[0],
				tolerance_minutes: this.newTolerance * this.toleranceStep,
				people: this.newPeople,
				name: this.newName,
				email: this.newEmail,
				phone: this.newPhone,
				children: this.newChildren,
				dogs: this.newDogs,
				notes: this.newNotes,
			};

			this.loading = true;
			const done = (r) => {
				this.loading = false;
				if (r && r.result && r.result === "OK") {
					this.roomsOrdersStore.getDayWaitlist(() => {
						this.notificationStore.showNotification("generic-success");
					});
					this.roomsOrdersStore.newWaitlistOpen = false;
					this.roomsOrdersStore.manageWaitlistEntry = null;
				} else {
					this.notificationStore.showNotification("generic-fail");
				}
			};

			if (this.editingEntry) {
				delete data.experience_id;
				API.init().updateWaitlistEntry(this.userStore.currBusinessId, this.editingEntry.waitlist_id, data, done);
			} else {
				API.init().newWaitlistManual(this.userStore.currBusinessId, data, done);
			}
		},
	},
	mounted() {
		if (this.experiencesStore.businessExperiences && this.experiencesStore.businessExperiences.length > 0) {
			this.newExperience = this.experiencesStore.businessExperiences[0].experience_id;
		}
		const entry = this.roomsOrdersStore.manageWaitlistEntry;
		if (entry) {
			if (this.experiencesStore.businessExperiences) {
				const match = this.experiencesStore.businessExperiences.find(e => Number(e.experience_id) === Number(entry.experience_id));
				if (match) { this.newExperience = match.experience_id; }
			}
			this.newDate = new Date(entry.fullDate);
			this.newTime = new Date(entry.fullDate);
			this.newPeople = entry.people;
			this.newName = entry.name;
			this.newEmail = entry.email;
			this.newPhone = entry.phone;
			this.newChildren = entry.children;
			this.newDogs = entry.dogs;
			this.newNotes = entry.notes;
			this.pendingToleranceMinutes = entry.tolerance_minutes;
		}
	},
	watch: {
		newExperience() {
			this.loadExperienceData();
		},
		selectedSlot(newSlot) {
			// When editing, translate the stored minutes back to a step multiple once the slot is known
			if(newSlot && this.pendingToleranceMinutes !== null) {
				const ratio = this.pendingToleranceMinutes / (newSlot.intervals || 15);
				this.newTolerance = this.pendingToleranceMinutes <= 0 ? 0 : (ratio <= 2 ? 1 : 3);
				this.pendingToleranceMinutes = null;
			}
		},
	}
}
</script>

<style scoped lang="scss">
.list-item-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	width: 100%;

	.list-item-title {
		margin-right: 5px;
	}

	.input-wrapper {
		width: 240px;
		margin-left: auto;

		@media (max-width: 1250px) {
			width: 190px;
		}
	}

	.no-time-available {
		font-size: 16px;
		color: var(--color-text-primary);
		background-color: var(--color-bg-primary-2);
		border-radius: 13px;
		padding: 6px 10px;
		min-height: 35px;
		width: fit-content;
	}

	.touch-number-input-container {
		margin-left: auto;

		.input-wrapper {
			width: auto !important;
		}
	}
}
</style>
